import React, { useState } from "react";
import { Form } from "react-final-form";
import { useSelector } from "react-redux";
import { Alert, Box, Button } from "@mui/material";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import { selectFoldersForAddingProject, useUpdateProjectMutation } from "fond/api";
import { HOME_FOLDER_ID } from "fond/constants";
import { FolderPickerField } from "fond/form/fields/FolderPickerField";
import { ProjectPreview, Store } from "fond/types";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { Modal } from "fond/widgets";

interface IFormData {
  FolderId: string;
}

interface IProps {
  project: ProjectPreview;
  onClose(): void;
}

const MoveProjectDialog: React.FC<IProps> = ({ project, onClose }: IProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const folders = useSelector((state: Store) => selectFoldersForAddingProject(state, project));
  const canMove = permissionCheck(project?.Permission.Level, Actions.PROJECT_MOVE);
  const [updateProject] = useUpdateProjectMutation();

  /**
   * On submit function called when the form is submitted and valid
   */
  const onSubmit = async (values: IFormData) => {
    const folderId = values.FolderId;
    setSaving(true);

    try {
      await updateProject({ ID: project.ID, FolderID: folderId === HOME_FOLDER_ID ? null : folderId }).unwrap();
      enqueueSnackbar("The Project has been moved");
    } catch {
      enqueueSnackbar("Project move failed. Please try again...");
    } finally {
      setSaving(false);
      onClose();
    }
  };

  return (
    <Modal
      open
      header="Move this project"
      data-testid="move-folder-modal"
      content={
        !canMove ? (
          <Box mb={2}>
            <Alert severity="info">You do not have permission to move this project.</Alert>
          </Box>
        ) : (
          <>
            <Box mb={2} data-testid="move-alert-message">
              <Alert severity="warning">Moving a project to a new location has the potential to change who can access & edit this project.</Alert>
            </Box>
            <Form<IFormData>
              onSubmit={onSubmit}
              initialValues={{ FolderId: project.FolderID || HOME_FOLDER_ID }}
              render={({ handleSubmit }) => {
                return (
                  <form onSubmit={handleSubmit} id="move-entity-form">
                    <FolderPickerField name="FolderId" folders={folders} account={project.Account} />
                  </form>
                );
              }}
            />
          </>
        )
      }
      actions={
        <>
          <Button data-testid="move-folder-cancel-button" color="primary" onClick={onClose} sx={{ marginRight: 1 }}>
            Cancel
          </Button>
          {canMove && (
            <LoadingButton form="move-entity-form" type="submit" data-testid="move-folder-save-button" color="primary" loading={saving}>
              Update
            </LoadingButton>
          )}
        </>
      }
    />
  );
};

export default MoveProjectDialog;
